import Alert from "@/common/components/Alert";
import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ShowHeader } from "@/common/components/ShowHeader";
import { AlertCircleIcon } from "lucide-react";
import { useParams } from "react-router-dom";
import BillingDynamicGroupableItem from "./components/BillDynamicGroupableItem";
import { billDynamicGroupablesUpdateSchema, BillDynamicGroupablesUpdateValidationSchema } from "@/features/privateCompanies/schemas/billDynamicGroupables/update.schema";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useInternalFieldArray } from "@/common/hooks/useInternalFieldArray";
import { useUpsertBillDynamicGroupables } from "@/features/privateCompanies/hooks/billDynamicGroupables/useUpsertBillDynamicGroupables";
import { useGoBack } from "@/common/hooks/useGoBack";
import { useQueryClient } from "@tanstack/react-query";
import useCompany from "@/common/hooks/queries/useCompany";
import { useClientBillDynamicGroupables } from "@/features/privateCompanies/hooks/billDynamicGroupables/useClientBillDynamicGroupables";
import { BillDynamicGroupablesData } from "@/features/privateCompanies/types/billDynamicGroupables.type";
import { useEffect, useState } from "react";
import { Mapper } from "@/common/services/mapper";
import Skeleton from "@/common/components/Skeleton";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";

const BillingDynamicGroupablesUpdate = () => {
  const { id } = useParams();

  const goBack = useGoBack();

  const queryClient = useQueryClient();

  const [showLoading, setShowLoading] = useState(true);

  const [groupsToDelete, setGroupsToDelete] = useState<{ id: string; _destroy: boolean }[]>([]);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    data: company,
    isPending: companyIsPending,
  } = useCompany({ id: id || '' });

  const {
    data: billDynamicGroupables,
    isPending: billDynamicGroupablesIsPending,
    error: billDynamicGroupablesError = {} as any,
    refetch: refetchBillDynamicGroupables,
  } = useClientBillDynamicGroupables<BillDynamicGroupablesData[]>({
    companyId: id,
  });

  const formMethods = useForm<BillDynamicGroupablesUpdateValidationSchema>({
    resolver: zodResolver(billDynamicGroupablesUpdateSchema),
  });

  const {
    fields: groupables,
    append: appendGroupable,
    remove: removeGroup,
    update: updateGroup,
  } = useInternalFieldArray<BillDynamicGroupablesUpdateValidationSchema, 'groupable'>({
    ...formMethods,
    name: 'groupable',
  });

  const {
    mutate: upsertBillDynamicGroupables,
    isPending: upsertBillDynamicGroupablesIsPending,
  } = useUpsertBillDynamicGroupables({
    companyId: id,
    groupsToDelete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [''] });

      goBack({ fallback: `/companies/${id}/politics`, searchLastRouteFromFallback: true });
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setMessages, setError: formMethods.setError })(error);
    },
  });

  const onSubmit: SubmitHandler<BillDynamicGroupablesUpdateValidationSchema> = (data) => upsertBillDynamicGroupables(data);

  useEffect(() => {
    if (!billDynamicGroupablesIsPending) {
      formMethods.reset({
        groupable: billDynamicGroupables.map((group) => ({
          id: group.id,
          categories: Mapper.mapToOptions({ labelFieldName: 'display_name', valueFieldName: 'id' })(group.product_categories),
          products: Mapper.mapToOptions({ labelFieldName: 'display_name', valueFieldName: 'id' })(group.products),
        })),
      });

      setTimeout(() => setShowLoading(false));
    }
  }, [billDynamicGroupablesIsPending]);

  return (
    <FormProvider {...formMethods}>
      <div>
        <ShowHeader
          goBack
          goBackProps={{ fallback: `/companies/${id}/politics`, searchLastRouteFromFallback: true }}
          isLoading={companyIsPending}
          title={company?.general_information?.fantasy_name}
          subtitle="Agrupamento Dinâmico de Fatura"
          titleClassName="text-md"
          subtitleClassName="text-nowrap font-medium"
          buttons={(
            <div className="flex items-center gap-2">
              <Button
                variant="outline-error"
                onClick={() => goBack({ fallback: `/companies/${id}/politics`, searchLastRouteFromFallback: true })}
              >
                Cancelar
              </Button>

              <Button
                name="submit"
                isLoading={upsertBillDynamicGroupablesIsPending}
                onClick={formMethods.handleSubmit(onSubmit)}
                operationKlassToCheckPermission="Admin::Client::Companies::UpsertBillDynamicGroupables"
              >
                Salvar
              </Button>
            </div>
          )}
        />

        <ErrorMessageWrapper
          className="h-[calc(100vh-128px)]"
          statusCode={billDynamicGroupablesError?.status}
          refetchFunction={refetchBillDynamicGroupables}
        >
          <Box className="flex flex-col gap-4 h-[calc(100vh-128px)] overflow-y-auto">
            <div className="flex items-center justify-between">
              <p>
                Agrupamento Dinâmico de Fatura
              </p>

              <Button
                name="add_groupable"
                onClick={() => {
                  appendGroupable({
                    products: [],
                    categories: [],
                  });
                }}
              >
                Novo agrupamento
              </Button>
            </div>

            <Box className="flex items-center justify-between p-4">
              <p>
                Demais faturas
              </p>

              <Alert
                variant="warning"
                Icon={AlertCircleIcon}
                containerClassName="bg-transparent p-0"
              >
                <div className="flex flex-col">
                  <p className="text-xs">
                    Demais faturas serao emitidas pelo agrupamento definido no contrato.
                  </p>

                  <p className="text-xs italic font-light">
                    Atente-se, os Serviços e Produtos específicados em faturas abaixo não entrarão nas demais faturas.
                  </p>
                </div>
              </Alert>
            </Box>

            {showLoading && new Array(2).fill(null).map((_, i) => (
              <Box key={`loading_${i}`} className="p-4 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <Skeleton width={100} height={24} />

                  <Skeleton width={120} height={40} className="bg-white-100" />
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-full space-y-1.5">
                    <Skeleton width={100} height={16} />

                    <Skeleton className="bg-white-100" height={48} />
                  </div>

                  <div className="w-full space-y-1.5">
                    <Skeleton width={100} height={16} />

                    <Skeleton className="bg-white-100" height={48} />
                  </div>
                </div>
              </Box>
            ))}

            {!showLoading && groupables.map((group, index) => (
              <BillingDynamicGroupableItem
                key={`groupable_${index}`}
                group={group}
                index={index}
                updateGroup={(data) => updateGroup(index, data)}
                removeGroup={() => {
                  removeGroup(index);

                  if (group.id)
                    setGroupsToDelete((prev) => [...prev, { id: group.id!, _destroy: true }]);
                }}
                selectedCategories={groupables.flatMap((g) => g.categories.map(c => c.value))}
                selectedProducts={groupables.flatMap((g) => g.products.map(p => p.value))}
              />
            ))}
          </Box>
        </ErrorMessageWrapper>
      </div>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </FormProvider>
  );
}

export default BillingDynamicGroupablesUpdate;
